import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import {
  Box,
  FormControlLabel,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { handleErrorToast } from 'src/components/molecules';
import {
  CategoryConfigurationStatus,
  GetCategoryConfigCategoriesQuery,
  useGetCategoryConfigCategoriesQuery,
  useUpdateCategoryMutation,
} from 'src/graphql/generated';
import { CategoryDrawerShell } from '../molecules/CategoryDrawerShell';
import { FormUnpublishedModal } from '../molecules/FormUnpublishedModal';
import { DiscardChangesModal } from './DiscardChangesModal';

const MAX_NAME_LENGTH = 140;
const DEFAULT_LOCALE = 'en-GB';

type Category = NonNullable<
  GetCategoryConfigCategoriesQuery['getOrganisationCategories']
>[number];

interface EditCoiCategoryDrawerProps {
  category: Category;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const EditCoiCategoryDrawer: React.FC<EditCoiCategoryDrawerProps> = ({
  category,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { tokens } = useTheme<LensTheme>();
  const color = tokens.semantic.color;

  const baselineName =
    (category.locales as Record<string, { name?: string } | undefined>)?.[
      DEFAULT_LOCALE
    ]?.name ?? '';

  const baselineActive = category.status === CategoryConfigurationStatus.Active;
  // The toggle tracks whether the category is reported (on) vs deactivated
  // (off), not the derived Active/FormRequired/Inactive status. A
  // FormRequired category is already reported, so baseline must reflect
  // isReportedCategory — otherwise a name-only save would send
  // isReportedCategory: false and flip the status to Inactive.
  const baselineReported = category.isReportedCategory;

  const [name, setName] = useState(baselineName);
  const [isActiveToggle, setIsActiveToggle] = useState(baselineReported);
  const [nameError, setNameError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);

  useEffect(() => {
    setName(baselineName);
    setIsActiveToggle(baselineReported);
    setNameError('');
    setIsDiscardModalOpen(false);
    setIsDeactivateModalOpen(false);
    setIsSaving(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category.id]);

  const queryClient = useQueryClient();
  const { data } = useGetCategoryConfigCategoriesQuery({ isCOI: true });
  const categories = data?.getOrganisationCategories ?? [];
  const { mutateAsync: updateCategory } = useUpdateCategoryMutation();

  const existingDescription =
    (
      category.locales as
        | Record<string, { description?: string } | undefined>
        | undefined
    )?.[DEFAULT_LOCALE]?.description ?? '';

  const isDuplicateName = (newName: string): boolean => {
    if (!newName.trim()) return false;
    return categories.some(
      (cat) =>
        cat.id !== category.id &&
        cat.locales?.[DEFAULT_LOCALE]?.name === newName.trim(),
    );
  };

  const handleNameChange = (value: string) => {
    const trimmed = value.slice(0, MAX_NAME_LENGTH);
    setName(trimmed);
    if (nameError && !isDuplicateName(trimmed)) {
      setNameError('');
    }
  };

  const commitSave = async () => {
    if (isSaving) return;
    const deactivate = !isActiveToggle;
    try {
      setIsSaving(true);
      await updateCategory({
        category: {
          id: category.id,
          isReportedCategory: !deactivate,
          isDeterminedCategory: category.isDeterminedCategory,
          displayInApp: !deactivate,
          displayInManualIntake: category.displayInManualIntake,
          displayInOpenReporting: !deactivate,
          displayInVaultTalk: category.displayInVaultTalk,
          displayInCollaborationToolIntegration:
            category.displayInCollaborationToolIntegration,
          locales: {
            [DEFAULT_LOCALE]: {
              name: name.trim(),
              description: existingDescription,
            },
          },
        },
      });
      queryClient.refetchQueries(['GetCategoryConfigCategories']);
      setIsDeactivateModalOpen(false);
      onClose();
      onSuccess?.();
    } catch (error) {
      console.error('Error updating category', {
        categoryId: category.id,
        deactivate,
        error,
      });
      handleErrorToast({ toastMessage: 'Error updating category' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = () => {
    if (name.trim() && isDuplicateName(name.trim())) {
      setNameError(
        'Category name already exists. Please enter a unique name or modify the existing category.',
      );
      return;
    }
    if (isActiveToggle !== baselineReported) {
      setIsDeactivateModalOpen(true);
      return;
    }
    void commitSave();
  };

  const hasUnsavedChanges =
    name.trim() !== baselineName || isActiveToggle !== baselineReported;

  const handleRequestClose = () => {
    if (hasUnsavedChanges) {
      setIsDiscardModalOpen(true);
    } else {
      onClose();
    }
  };

  return (
    <>
      <CategoryDrawerShell
        open={isOpen}
        title="Edit category"
        onRequestClose={handleRequestClose}
        onSave={handleSave}
        saveDisabled={!name.trim() || isSaving || !hasUnsavedChanges}
        testId="edit-coi-category-drawer"
      >
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              mb: '4px',
            }}
          >
            <Typography
              component="label"
              sx={{
                fontSize: '14px',
                fontWeight: tokens.semantic.fontWeight.emphasis.value,
                color: color.type.default.value,
              }}
            >
              Category name{' '}
              <span style={{ color: color.type.muted.value }}>*</span>
            </Typography>
            <Typography
              component="span"
              sx={{ fontSize: '12px', color: color.type.muted.value }}
            >
              {name.length}/{MAX_NAME_LENGTH}
            </Typography>
          </Box>
          <TextField
            fullWidth
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            data-testid="edit-coi-category-name-input"
            inputProps={{ maxLength: MAX_NAME_LENGTH }}
            error={!!nameError}
          />
          {nameError && (
            <Typography
              sx={{
                fontSize: '12px',
                color: color.status.error.default.value,
                mt: '4px',
              }}
              data-testid="edit-coi-category-name-error"
            >
              {nameError}
            </Typography>
          )}
        </Box>

        {(baselineActive ||
          category.status === CategoryConfigurationStatus.Inactive ||
          category.status === CategoryConfigurationStatus.FormRequired) && (
          <Tooltip
            title={
              category.hasActiveLinkedForm
                ? 'Unpublish the linked form before deactivating this category'
                : ''
            }
          >
            <span
              tabIndex={category.hasActiveLinkedForm ? 0 : undefined}
              data-testid="edit-coi-category-deactivate-toggle-tooltip-trigger"
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={!isActiveToggle}
                    onChange={(e) => setIsActiveToggle(!e.target.checked)}
                    disabled={Boolean(category.hasActiveLinkedForm)}
                    data-testid="edit-coi-category-deactivate-toggle"
                  />
                }
                label="Deactivate category"
                sx={{
                  m: 0,
                  gap: '8px',
                  '& .MuiFormControlLabel-label': {
                    fontSize: '14px',
                    fontWeight: tokens.semantic.fontWeight.emphasis.value,
                    color: color.type.default.value,
                  },
                }}
              />
            </span>
          </Tooltip>
        )}

        {(isActiveToggle ||
          category.status === CategoryConfigurationStatus.FormRequired) && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px',
              borderRadius: '12px',
              border: `1px solid ${color.ui.divider.default.value}`,
            }}
            data-testid="edit-coi-category-intake-channel"
          >
            <Box>
              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: tokens.semantic.fontWeight.emphasis.value,
                  color: color.type.default.value,
                }}
              >
                Intake channel
              </Typography>
              <Typography
                sx={{ fontSize: '14px', color: color.type.muted.value }}
              >
                Conflict of interest disclosures can only be submitted via Vault
              </Typography>
            </Box>
          </Box>
        )}
      </CategoryDrawerShell>

      <DiscardChangesModal
        open={isDiscardModalOpen}
        onClose={() => setIsDiscardModalOpen(false)}
        onConfirm={() => {
          setIsDiscardModalOpen(false);
          onClose();
        }}
      />

      <FormUnpublishedModal
        open={isDeactivateModalOpen}
        variant={isActiveToggle ? 'reactivate' : 'deactivate'}
        onClose={() => {
          setIsDeactivateModalOpen(false);
          setIsActiveToggle(baselineReported);
        }}
        onConfirm={() => void commitSave()}
      />
    </>
  );
};
